<template>
  <div ref="container" :class="viewClassNames">
    <div class="toolbar-backdrop" ref="toolbar-backdrop" :style="toolbarBackdropStyles"></div>
    <CMToolbar
      :class="toolbarClassNames"
      :nav="toolbarNavItem"
      :spacer="false"
      :title="recipe.parentedTitle"
      ref="toolbar">
      <CMButton
        v-for="(item, index) in toolbarActionItemsGrouped.always"
        :key="index"
        v-bind="item"></CMButton>
      <MUIMenu :items="toolbarActionItemsGrouped.never" anchor="right">
        <CMButton icon="more_vert" name="More" slot="button"></CMButton>
      </MUIMenu>
    </CMToolbar>
    <div
      class="layout__recipe layout__recipe--compact"
      id="sheet"
      ref="scrollpane"
      @click="onTap">
      <div class="view--recipe-container" style="position: relative">
        <header ref="header" class="recipe-header">
          <div ref="header-meta" class="recipe-header__expansion-container">
            <h1 :class="`title--${recipe.name && recipe.name.length > 64 ? 'long' : 'short'}`">{{recipe.parentedTitle || recipe.name || recipe.resolvedUrl || recipe.givenUrl}}</h1>
            <section class="meta">
              <RecipeMeta v-bind="recipe.metadata"></RecipeMeta>
            </section>
            <section class="collections" style="margin-top: 12px;">
              <MUIChip
                v-for="item in collections"
                :key="item.key"
                @click.native="transitionToCollection(item.key, recipe.key)"
              >{{item.name}}</MUIChip>
            </section>
          </div>
          <MUITabBar
            ref="tabbar"
            :panels="visiblePanels"
            :panel="panel"
            :scrolled="panelScrolled"
            :width="$store.state.device.innerWidth"
            v-on:push="pushPanel"
            v-if="!recipe.isBlank"></MUITabBar>
        </header>
        <Notice :data="notices.missingSchema" v-if="recipe.notFound"></Notice>
        <div
          class="panels"
          ref="panels" v-if="!recipe.isBlank">
          <MUIPanel
            v-if="panel === 'ingredients'"
            ref="ingredients">
            <ScrollLayout
              :onTouchMove="onTouchMove"
              :onTouchStart="onTouchStart"
              :onTouchEnd="onTouchEnd"
              :style="panelStyles">
              <Ingredients :items="recipe.ingredients" :scale="scale" />
            </ScrollLayout>
          </MUIPanel>
          <MUIPanel
            v-if="panel === 'instructions'"
            ref="instructions">
            <ScrollLayout
              :onTouchMove="onTouchMove"
              :onTouchStart="onTouchStart"
              :onTouchEnd="onTouchEnd"
              :style="panelStyles">
              <Instructions :items="recipe.instructions" />
            </ScrollLayout>
          </MUIPanel>
          <MUIPanel
            v-if="panel === 'notes'"
            ref="notes">
            <ScrollLayout
              :onTouchMove="onTouchMove"
              :onTouchStart="onTouchStart"
              :onTouchEnd="onTouchEnd"
              :style="panelStyles">
              <section>
                <p v-for="(p, index) in recipe.notes" :key="index">{{p}}</p>
              </section>
            </ScrollLayout>
          </MUIPanel>
        </div>
      </div>
    </div>
    <div class="recipe-cover" ref="cover">
      <progressive-background
        :src="recipe.imageUrl('preview')"
        :placeholder="recipe.imageUrl('placeholder')"
        :aspect-ratio="0.75" />
    </div>
  </div>
</template>

<script>
import {
  Ingredients,
  Instructions } from '@/components/Recipe'
import Notice from '../components/Notice'
import ScrollLayout from '../layouts/ScrollLayout'
import RecipeMeta from '../components/RecipeMeta'
import MUIChip from '../components/MUIChip'
import MUIMenu from '../components/MUIMenu'
import MUIPanel from '../components/MUIPanel'
import MUITabBar from '../components/MUITabBar'
import MUITruncate from '../components/MUITruncate'
import CMToolbar from '../components/CMToolbar'
import CMButton from '../components/CMButton'

const FAST = 'fast'
const NORMAL = 'normal'
const ANIMATION_DURATION = {
  'normal': 480,
  'fast': 480
}

export default {
  name: 'recipe-detail-compact',
  components: {
    CMButton,
    CMToolbar,
    Notice,
    RecipeMeta,
    MUIChip,
    MUIMenu,
    MUIPanel,
    MUITabBar,
    MUITruncate,
    ScrollLayout,
    Ingredients,
    Instructions
  },
  props: {
    collections: {
      type: Array,
      default: () => []
    },
    enlargeImage: Function,
    hidePrimaryHandler: Function,
    showPrimaryHandler: Function,
    notices: Object,
    recipe: Object,
    toolbarActionItemsGrouped: Object,
    transitionToCollection: Function,
    onSelectImage: Function,
    scale: String
  },
  data () {
    return {
      debug: null,
      observers: {
        cover: null,
        snapWindow: null
      },
      panel: 'ingredients',
      panels: {
        ingredients: {
          name: 'Ingredients',
          position: 0,
          scrollPos: 0,
          touchStartY: null
        },
        instructions: {
          name: 'Instructions',
          position: 1,
          scrollPos: 0,
          touchStartY: null
        },
        notes: {
          name: 'Notes',
          position: 2,
          scrollPos: 0,
          touchStartY: null
        }
      },
      scrollPos: 0,
      sheet: {
        isExpanded: false,
        coverHeight: 201,
        expandedY: null,
        panelPadding: 24,
        // Reset at the start of every pan to ensure the sheet doesn't jump
        panStartY: 201,
        snapDirection: null,
        // Snap closed when beneath this position while scrolling down
        snapOffsetTop: 31,
        // Snap open/closed when either side of this position
        snapOffsetBottom: 121
      },
      tabbar: {
        height: 48
      },
      toolbar: {
        intersecting: false,
        maximumHeight: 180,
        minimumHeight: 61,
        theme: 'dark',
        transparent: true
      },
      touch: {
        isDragging: false,
        startY: null
      }
    }
  },
  computed: {
    deviceHeight () {
      return this.$store.state.device.innerHeight
    },
    headerHeight () {
      return this.toolbar.minimumHeight + this.tabbar.height
    },
    currentPanel () {
      return this.panels[this.panel]
    },
    panelHeight () {
      return this.$store.state.device.innerHeight - this.headerHeight
    },
    panelScrolled () {
      return this.panels[this.panel].scrollPos > 0
    },
    panelStyles () {
      return {
        height: `${this.panelHeight - 2 * this.sheet.panelPadding}px`,
        padding: `${this.sheet.panelPadding}px`,
        overflowY: this.sheet.isExpanded ? 'auto' : 'hidden'
      }
    },
    sheetContractedY () {
      return this.recipe.hasImage ? this.sheet.coverHeight : 0
    },
    sheetExpandedY () {
      let y = this.toolbar.minimumHeight - this.$refs.header.clientHeight
      // if recipe found, include the tabbar height
      if (!this.recipe.notFound) y += this.tabbar.height
      if (!this.recipe.hasImage) y -= 42
      return y
    },
    toolbarBackdropStyles () {
      return {
        opacity: !this.recipe.hasImage || this.toolbar.intersecting ? 1 : 0
      }
    },
    toolbarClassNames () {
      return {
        'mui-toolbar--transparent': true,
        'mui-theme--dark': this.toolbar.theme === 'dark' && this.recipe.hasImage,
        'mui-toolbar--recipe': true,
        'mui-toolbar--flexed': true
      }
    },
    toolbarNavItem () {
      return {
        icon: 'keyboard_arrow_down',
        fn: () => { this.showPrimaryHandler() }
      }
    },
    viewClassNames () {
      return {
        'has-image': this.recipe.hasImage,
        'has-no-image': !this.recipe.hasImage,
        'is-expanded': this.sheet.isExpanded
      }
    },
    visiblePanels () {
      let panels = {
        ingredients: this.panels.ingredients,
        instructions: this.panels.instructions
      }
      // TODO: Yuck
      if (this.recipe.notes && this.recipe.notes !== '') panels.notes = this.panels.notes
      return panels
    }
  },
  methods: {
    getSheetY () {
      if (!this.$refs.scrollpane) return
      return this.$refs.scrollpane.getBoundingClientRect().y
    },
    setSheetExpandedY () {
      let y = this.toolbar.minimumHeight +
        this.tabbar.height -
        this.$refs.header.clientHeight
      if (!this.recipe.hasImage) y -= 42
      return y
    },
    onPanEnd (ev) {
      if (this.sheet.snapDirection === 'expand') {
        this.resolveToExpanded(FAST)
      } else if (this.sheet.snapDirection === 'contract') {
        this.resolveToContracted(FAST)
      } else {
        this.sheet.isExpanded
          ? this.resolveToContracted(FAST)
          : this.resolveToExpanded(FAST)
      }
    },
    onTap (ev) {
      if (
        this.sheet.isExpanded ||
        ['A', 'INPUT'].indexOf(ev.target.nodeName) !== -1 ||
        ev.target.classList.contains('mui-chip__text')
      ) return

      this.resolveToExpanded(FAST)
    },
    resolveToContracted (speed = NORMAL) {
      let ref = this.$refs.scrollpane
      let onTransitionEnd = (ev) => {
        this.sheet.panStartY = this.getSheetY()
        ref.style.transitionDuration = 'initial'
        this.sheet.isExpanded = false
        ref.removeEventListener('transitionend', onTransitionEnd)
      }
      if (this.getSheetY() === this.sheetContractedY) return onTransitionEnd()

      ref.addEventListener('transitionend', onTransitionEnd, false)
      ref.style.transitionDuration = `${ANIMATION_DURATION[speed] / 1000}s`
      ref.style.transform = `translateY(${this.sheetContractedY}px)`
    },
    resolveToExpanded (speed = NORMAL) {
      let ref = this.$refs.scrollpane
      let onTransitionEnd = (ev) => {
        this.sheet.panStartY = this.getSheetY()
        ref.style.transitionDuration = 'initial'
        this.sheet.isExpanded = true
        ref.removeEventListener('transitionend', onTransitionEnd)
      }
      if (this.getSheetY() === this.sheetExpandedY) return onTransitionEnd()

      ref.addEventListener('transitionend', onTransitionEnd, false)
      let duration = `${ANIMATION_DURATION[speed] / 1000}s`
      ref.style.transitionDuration = duration
      ref.style.transform = `translateY(${this.sheetExpandedY}px)`
    },
    pushPanel (panel) {
      // TODO: Don't use children, fragile!
      this.panels[this.panel].scrollPos = this.$refs[this.panel].$children[0].$el.scrollTop
      this.panel = panel
      let nextScrollPos = this.panels[this.panel].scrollPos
      this.$nextTick(() => {
        this.$refs[panel].$children[0].$el.scrollTop = nextScrollPos
      })
    },
    onTouchStart (ev) {
      if (this.$refs[this.panel]) {
        this.touch.scrollPos = this.$refs[this.panel].$children[0].$el.scrollTop
      }
      this.touch.startY = ev.touches[0].clientY
    },
    onTouchEnd (ev) {
      if (this.touch.isDragging) {
        this.onPanEnd(ev)
        this.touch.isDragging = false
      }
      this.touch.direction = undefined
      this.sheet.panStartY = this.getSheetY()
    },
    onTouchMove (ev) {
      this.setTouchEventProperties(ev)
      // let panelScrollTop = this.$refs[this.panel].$children[0].$el.scrollTop
      let offsetY = this.touch.startY - ev.touches[0].clientY + this.touch.scrollPos
      this.transformSheet(offsetY)
    },
    setToolbarTitleOpacity (opacity) {
      this.$refs.toolbar.$refs.title.style.opacity = opacity
    },
    transformSheet (offsetY) {
      let calcYPos = this.sheet.panStartY - offsetY
      let yPos = Math.min(
        Math.max(calcYPos, this.sheetExpandedY),
        this.sheetContractedY
      )
      this.$refs.scrollpane.style.transform = `translateY(${yPos}px)`
    },
    setTouchEventProperties (ev) {
      let offsetVertical = this.touch.startY - ev.touches[0].clientY
      this.touch.direction = offsetVertical > 0 ? 'up' : 'down'
      if (this.touch.isDragging) return

      // If exceeds drag threshold
      this.touch.isDragging = Math.abs(offsetVertical) > 5
    }
  },
  watch: {
    deviceHeight () {
      this.setSheetExpandedY()
    },
    'sheet.isExpanded' () {
      if (this.recipe.hasImage) return
      this.setToolbarTitleOpacity(this.sheet.isExpanded ? 1 : 0)
    },
    'toolbar.intersecting' () {
      if (!this.recipe.hasImage) return
      this.setToolbarTitleOpacity(this.toolbar.intersecting ? 1 : 0)
    },
    'recipe.hasImage' () {
      // TODO: Prefer to use classes to set the correct position, rather than
      // calling this function, but the translateY value is defined inline.
      this.resolveToContracted(FAST)
    }
  },
  mounted () {
    document.title = `${this.recipe.name} - Cookmark`
    this.sheet.expandedY = this.setSheetExpandedY()

    let ref = this.$refs.scrollpane

    let snapWindow = this.sheet.snapOffsetBottom - this.sheet.snapOffsetTop
    let lowerThreshold = 0
    let upperThreshold = snapWindow / ref.clientHeight
    let threshold = [lowerThreshold, upperThreshold]

    this.observers.snapWindow = new IntersectionObserver(entries => {
      const [{ intersectionRatio }] = entries
      if (intersectionRatio === 0) {
        this.sheet.snapDirection = 'contract'
      } else if (intersectionRatio > upperThreshold) {
        this.sheet.snapDirection = 'expand'
      } else {
        this.sheet.snapDirection = 'dynamic'
      }
    }, {
      rootMargin: `0px 0px -${window.innerHeight - this.sheet.snapOffsetBottom}px 0px`,
      threshold: threshold
    })
    if (this.recipe.hasImage) this.observers.snapWindow.observe(ref)

    let coverWindow = this.sheet.snapOffsetBottom - 61
    let coverUpperThreshold = coverWindow / ref.clientHeight
    let buildThresholdList = (upper) => {
      let thresholds = []
      let numSteps = 20
      for (let i = 0.0; i <= numSteps; i++) {
        let ratio = upper / 20 * i
        thresholds.push(ratio)
      }
      return thresholds
    }
    let coverThreshold = buildThresholdList(coverUpperThreshold)

    this.observers.cover = new IntersectionObserver(entries => {
      const [{ intersectionRatio }] = entries
      let ratio = Math.min(intersectionRatio / coverUpperThreshold, 1)
      let boxShadow = '0px 0px 12px -8px black'
      ref.style.boxShadow = ratio > 0 ? 'none' : boxShadow
      this.toolbar.intersecting = ratio === 1
      this.toolbar.theme = ratio >= 0.6 ? 'light' : 'dark'
      this.$refs.cover.style.opacity = 1 - ratio
    }, {
      rootMargin: `0px 0px -${window.innerHeight - this.sheet.snapOffsetBottom}px 0px`,
      threshold: coverThreshold
    })
    if (this.recipe.hasImage) this.observers.cover.observe(ref)

    this.$refs.header.addEventListener('touchmove', this.onTouchMove, {
      passive: true
    })
    this.$refs.header.addEventListener('touchstart', this.onTouchStart, {
      passive: true
    })
    this.$refs.header.addEventListener('touchend', this.onTouchEnd, {
      passive: true
    })
  },
  destroyed () {
    this.observers.cover.disconnect()
    this.observers.snapWindow.disconnect()
    if (!this.$refs.header) return
    this.$refs.header.removeEventListener('touchmove', this.onTouchMove)
    this.$refs.header.removeEventListener('touchstart', this.onTouchStart)
    this.$refs.header.removeEventListener('touchend', this.onTouchEnd)
  }
}
</script>

<style lang="sass">
@import "../styles/variables.scss"
.no-mode-translate-fade-enter-active, .no-mode-translate-fade-leave-active
  display: inline-block
.no-mode-translate-fade-enter, .no-mode-translate-fade-leave-active
  opacity: 0

.mui-toolbar--recipe
  pointer-events: none
  position: absolute
  z-index: 1000
  .cm-button, .mui-toolbar__nav, a
    pointer-events: auto
.toolbar-backdrop
  height: 61px
  pointer-events: none
  will-transform: opacity
  background: #ffffff
  width: 100%
  position: absolute
  z-index: 10
.layout__split--collapsed
  .layout__split-detail
    width: 100%
    .mui-toolbar__title
      opacity: 0
    &.is-animating--fast
      .mui-toolbar__title
        transition: none
    .mui-toolbar:not(.mui-toolbar--transparent)
      border-bottom-color: transparent
  .layout__recipe.layout__recipe--compact
    box-shadow: 0px 0px 12px -8px rgba(0,0,0,1)
    transform: translateY(201px) translateZ(0)
    transition-timing-function: 'ease-out'
    will-change: transform
  .view--recipe-container
    background: #ffffff
    border-top-right-radius: 8px
    border-top-left-radius: 8px
  .has-no-image
    .layout__recipe.layout__recipe--compact
      transform: translateY(0px) translateZ(0)
      box-shadow: none
    .view--recipe-container
      padding-top: 42px
      border-radius: 0
  .toolbar-background
    background: #ffffff
    width: calc(100% - 16px)
    position: absolute
    z-index: 10
    left: 10px
    top: 1px
  header
    padding-top: 1px
    h1
      color: $toolbarColor
      font-family: $headlineFontFamily
      font-size: 24px
      font-weight: 300
      line-height: 32px
      margin-bottom: 6px
      margin-right: 24px
      white-space: normal
      word-break: break-word
    .recipe-header__expansion-container
      margin: 0px 24px 12px
      opacity: 1
      transition: opacity 0.5s
  .notice-card
  .recipe__collections
    margin-top: 16px
    padding-bottom: 16px
  .description
    margin-top: 12px
    position: relative
  .panels
    position: relative
    z-index: 10
    transition: left .32s
  .panel
    background: #fff
    margin: 0 0 64px
    position: absolute
    & > ul, & > ol
      padding: 24px
.recipe-cover
  height: 210px
  position: absolute
  width: 100%
  top: 0
  z-index: -1
  &:after
    appearance: none
    position: absolute
    background-image: linear-gradient(to bottom, rgba(0,0,0,0.3) 0, transparent 80px)
    top: 0
    height: 80px
    width: 100%
    content: ''
    z-index: 2
</style>



// WEBPACK FOOTER //
// src/components/RecipeDetailCompact.vue